import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { t as uiText } from '../i18n';
import { commitUiLanguage } from '../i18n/commitUiLanguage';
import { isUiLanguage, noteExplicitUiLanguageChoice } from '../i18n/uiLanguage';
import { syncEmailVerification } from '../features/emailVerification/api';
import '../features/emailVerification/EmailVerification.css';

type ViewState =
  | { kind: 'working' }
  | { kind: 'success'; mode: string }
  | { kind: 'error'; message: string };

function errorMessageFor(code: string): string {
  switch (code) {
    case 'auth/expired-action-code':
      return uiText('This link has expired. Please resend the confirmation email from notreu and try again.');
    case 'auth/invalid-action-code':
      return uiText('This link is invalid or has already been used. If you have already verified, please sign in. Otherwise, resend the confirmation email.');
    case 'auth/user-disabled':
      return uiText('This account is disabled.');
    case 'auth/user-not-found':
      return uiText('The account for this link was not found.');
    default:
      return uiText('Could not process the link. Please try again later.');
  }
}

/**
 * メール確認 / メールアドレス変更リンクの受け口（/sys/auth-action）。
 * サーバーが Admin SDK で生成した oobCode を、このページで applyActionCode する。
 */
const AuthActionPage: React.FC = () => {
  const [params] = useSearchParams();
  // メール内リンクの lang（ja / en）を、最初の描画より前に表示言語へ反映する。
  // 利用者の言語設定そのものは書き換えない（localStorage / DB には保存しない）
  useState(() => {
    const lang = params.get('lang');
    if (isUiLanguage(lang)) {
      noteExplicitUiLanguageChoice(); // ログイン済みでも DB の言語で上書きされないようにする
      commitUiLanguage(lang, { cache: false });
    }
    return null;
  });
  const [view, setView] = useState<ViewState>({ kind: 'working' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode / 再レンダーでの二重適用を防ぐ（oobCode は1回限り）
    started.current = true;

    const mode = params.get('mode') || '';
    const oobCode = params.get('oobCode') || '';
    if (!oobCode || (mode !== 'verifyEmail' && mode !== 'verifyAndChangeEmail')) {
      setView({ kind: 'error', message: uiText('This link is invalid. Please open the link from the email again.') });
      return;
    }

    (async () => {
      try {
        await applyActionCode(auth, oobCode);
      } catch (e) {
        setView({ kind: 'error', message: errorMessageFor((e as { code?: string })?.code || '') });
        return;
      }
      // 同じブラウザでログイン中なら最新状態を取り込み、サーバー側のキャッシュも失効させる
      try {
        const current = auth.currentUser;
        if (current) {
          await current.reload();
          await current.getIdToken(true);
          await syncEmailVerification();
        }
      } catch (e) {
        // メール変更直後はセッションが失効することがある。完了メッセージは出し、再ログインを案内する
        console.warn('[authAction] post-apply refresh failed', e);
      }
      setView({ kind: 'success', mode });
    })();
  }, [params]);

  return (
    <div className="ev-lock" role="main">
      <div className="ev-lock-card">
        {view.kind === 'working' && <p role="status">{uiText('Processing...')}</p>}
        {view.kind === 'success' && (
          <>
            <h1 className="ev-title">
              {view.mode === 'verifyAndChangeEmail'
                ? uiText('Your email address has been changed')
                : uiText('Your email address has been verified')}
            </h1>
            <p>
              {view.mode === 'verifyAndChangeEmail'
                ? uiText('If you are asked to sign in again, please use the new email address.')
                : uiText('You can now use notreu without restrictions.')}
            </p>
            <div className="ev-actions">
              <Link className="ev-btn ev-btn-primary" to="/">
                {uiText('Go to notreu')}
              </Link>
            </div>
          </>
        )}
        {view.kind === 'error' && (
          <>
            <h1 className="ev-title">{uiText('Could not complete verification')}</h1>
            <p role="alert">{view.message}</p>
            <div className="ev-actions">
              <Link className="ev-btn ev-btn-primary" to="/">
                {uiText('Go to notreu')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthActionPage;
